import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import {
  PiSpinnerGapLight,
  PiWarningLight,
} from 'react-icons/pi';
import Spinner from '../shared/TailwindComponents/Spinner';

interface DeleteReviewConfirmationProps {
  /** How many reviews are being deleted (for plural messaging) */
  reviewCount?: number;
  /** Names of reviews being deleted (for display) */
  reviewNames?: string[];
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const DeleteReviewConfirmation: React.FC<DeleteReviewConfirmationProps> = ({
  reviewCount = 1,
  reviewNames = [],
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const isPlural = reviewCount > 1;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <Spinner size="glyph" className="!h-12 !w-12" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t(`Deleting ${reviewCount} review sessions...`)
              : t('Deleting review session...')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-3 py-1 px-4">
        {/* Warning message */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/40">
          <PiWarningLight className="w-5 h-5 text-danger-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-danger-800 dark:text-danger-300">
              {isPlural
                ? t('This will permanently remove these review sessions and all associated findings, documents, and audit history.')
                : t('This will permanently remove this review session and all associated findings, documents, and audit history.')}
            </p>
          </div>
        </div>

        {/* Show review names if available */}
        {reviewNames.length > 0 && reviewNames.length <= 5 && (
          <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
              {isPlural ? t('Reviews to delete') : t('Review to delete')}
            </p>
            <ul className="space-y-1">
              {reviewNames.map((name, i) => (
                <li key={i} className="text-sm text-gray-900 dark:text-gray-100 truncate">
                  {name || t('Untitled review')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {reviewNames.length > 5 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t(`${reviewCount} review sessions selected for deletion.`)}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-4 pt-6 mt-2 border-t border-gray-300 dark:border-gray-700 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteReviewConfirmation;
